import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = BACKEND_URL;

const MLLearningProgress = () => {
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState(null);
  const [pairProgressionData, setPairProgressionData] = useState(null);
  const [learningAdjustments, setLearningAdjustments] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'pairs'

  useEffect(() => {
    fetchProgressData();
    fetchPairProgressionData();
    fetchLearningAdjustments();
    // Refresh every 60 seconds
    const interval = setInterval(() => {
      fetchProgressData();
      fetchPairProgressionData();
      fetchLearningAdjustments();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchProgressData = async () => {
    try {
      const token = localStorage.getItem('token');
      // v3.1: Real-time wick detection, 5-min scanning, 50% exhaustion threshold
      const response = await axios.get(`${API}/api/ml/learning-progress?system_version=v4.1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setProgressData(response.data);
        setError(null);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching ML progress:', err);
      setError('Failed to load ML progress data');
      setLoading(false);
    }
  };

  const fetchPairProgressionData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/ml/pair-win-rate-progression`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setPairProgressionData(response.data);
      }
    } catch (err) {
      console.error('Error fetching pair progression:', err);
    }
  };

  const fetchLearningAdjustments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/daily-reset-info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data && response.data.recent_learning_adjustments) {
        setLearningAdjustments(response.data.recent_learning_adjustments);
      }
    } catch (err) {
      console.error('Error fetching learning adjustments:', err);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem' }}>⏳</div>
        <div style={{ marginTop: '1rem', color: '#64748b' }}>Loading ML Learning Progress...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
        {error}
      </div>
    );
  }

  if (!progressData) {
    return null;
  }

  const { 
    stage_name, 
    stage_description, 
    stage_emoji, 
    temperature,
    progress_percent = 0,
    total_trades = 0,
    overall_win_rate = 0,
    recent_win_rate = 0,
    baseline_win_rate = 0,
    improvement = 0,
    expected_win_rate,
    expected_ml_impact,
    ml_trained = false,
    ml_accuracy = null,
    milestones = [],
    next_milestone = null,
    win_rate_trend = [],
    daily_stats = { trades: 0, wins: 0, losses: 0, win_rate: 0 },
    motivation_message,
    next_steps = []
  } = progressData;

  // Get stage color
  const getStageColor = () => {
    if (total_trades < 100) return '#3b82f6'; // Blue
    if (total_trades < 200) return '#f59e0b'; // Orange
    if (total_trades < 400) return '#10b981'; // Green
    return '#8b5cf6'; // Purple
  };

  const stageColor = getStageColor();

  return (
    <div style={{ 
      padding: '2rem',
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${stageColor} 0%, ${stageColor}dd 100%)`,
        borderRadius: '16px',
        padding: '2rem',
        color: 'white',
        marginBottom: '2rem',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>{stage_emoji}</div>
            <h1 style={{ fontSize: '2rem', fontWeight: 'bold', margin: '0 0 0.5rem 0' }}>
              {stage_name}
            </h1>
            <p style={{ fontSize: '1.1rem', opacity: 0.9, margin: 0 }}>
              {stage_description}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '3rem', fontWeight: 'bold' }}>{total_trades}</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Total Trades</div>
            <div style={{ marginTop: '0.5rem', fontSize: '1rem' }}>
              🌡️ {temperature}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: '0.5rem',
            fontSize: '0.9rem'
          }}>
            <span>Stage Progress</span>
            <span>{progress_percent.toFixed(1)}%</span>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.3)',
            borderRadius: '999px',
            height: '12px',
            overflow: 'hidden'
          }}>
            <div style={{
              background: 'white',
              height: '100%',
              width: `${progress_percent}%`,
              transition: 'width 0.5s ease',
              borderRadius: '999px'
            }} />
          </div>
        </div>
      </div>

      {/* Motivation Message */}
      <div style={{
        background: '#fef3c7',
        border: '2px solid #fbbf24',
        borderRadius: '12px',
        padding: '1rem 1.5rem',
        marginBottom: '2rem',
        fontSize: '1.1rem',
        color: '#92400e'
      }}>
        💡 <strong>{motivation_message}</strong>
      </div>

      {/* Recent Learning Adjustments Card */}
      {learningAdjustments && learningAdjustments.length > 0 && (
        <div style={{ 
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
          padding: '1.5rem', 
          borderRadius: '12px', 
          marginBottom: '2rem',
          color: 'white',
          boxShadow: '0 4px 6px rgba(139, 92, 246, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <span style={{ fontSize: '1.5rem' }}>🧠</span>
            <div>
              <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>Recent Learning Adjustments</div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                How the AI adapted based on past 24h performance
              </div>
            </div>
            <span style={{ 
              fontSize: '0.75rem', 
              background: 'rgba(255,255,255,0.2)', 
              padding: '0.375rem 0.75rem', 
              borderRadius: '6px',
              fontWeight: '600',
              marginLeft: 'auto'
            }}>
              {learningAdjustments.length} adjustments
            </span>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {learningAdjustments.map((adj, idx) => {
              // Get detailed explanation for the adjustment
              const getExplanation = (reason) => {
                const explanations = {
                  'POOR_ENTRY': 'Before: Entry timing was off - price moved 10-40% towards target but reversed before reaching TP. The ML initially calculated entry at a suboptimal price point. After: ML adjusted entry detection to wait for stronger confirmation signals and better price action patterns before recommending trade entry.',
                  'WRONG_DIRECTION': 'Before: Trade went against the predicted direction immediately - less than 10% favorable movement before reversing. The contrarian logic was triggered at the wrong market phase. After: ML adjusted direction detection to analyze longer timeframes and multiple indicators before confirming contrarian setups.',
                  'STOP_LOSS_TOO_TIGHT': 'Before: Stop loss was placed too close to entry, getting hit before meaningful price movement could occur. The risk calculation underestimated required breathing room. After: ML increased minimum SL distance based on pair volatility and average true range (ATR).',
                  'TAKE_PROFIT_TOO_CONSERVATIVE': 'Before: Take profit target was set too close to entry - price went well beyond target but trade closed early, missing larger gains. After: ML adjusted TP calculation to use wider support/resistance levels and extended profit targets for high-confidence setups.',
                  'LOW_CONFIDENCE_WIN': 'Before: Trade won but probability was below 70% threshold - essentially a lucky win that shouldn\'t have been executed. After: ML tightened entry criteria to avoid taking marginal setups and raised minimum confidence threshold.',
                  'HIGH_CONFIDENCE_LOSS': 'Before: High probability trade (>80%) still lost - market showed anomalous behavior contradicting all indicators. After: ML added volatility filters and news event detection to avoid trading during unpredictable conditions.',
                  'SCALP_FAILURE': 'Before: Scalp trade at current price failed due to quick reversal immediately after entry. The immediate market action wasn\'t sustainable. After: ML adjusted scalp entry to require stronger momentum confirmation and reduced scalp frequency during choppy markets.',
                  'MOMENTUM_SHIFT': 'Before: Market momentum changed mid-trade, reversing the expected direction. Initial analysis missed early signs of momentum exhaustion. After: ML enhanced momentum indicators to detect weakening trends earlier and avoid late-cycle entries.',
                  'VOLATILITY_SPIKE': 'Before: Unexpected volatility spike caused early SL hit. The trade setup didn\'t account for upcoming high-impact events. After: ML integrated volatility forecasting and economic calendar to avoid trading before major announcements.',
                  'CORRELATION_BREAK': 'Before: Expected currency correlation did not hold - related pairs moved independently contradicting historical patterns. After: ML adjusted correlation analysis to use shorter lookback periods and weight recent correlation data more heavily.'
                };
                return explanations[reason] || `Before: ${reason.replace(/_/g, ' ')} occurred. After: ML system adjusted its calculations based on this pattern.`;
              };

              const adjustmentText = adj.pair ? 
                `${adj.pair} ${adj.direction} - ${adj.count || 1} failure${(adj.count || 1) > 1 ? 's' : ''}` : 
                adj.reason;

              return (
                <div key={idx} style={{ 
                  background: 'rgba(255,255,255,0.1)', 
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <span style={{ 
                      background: adj.severity === 'HIGH' ? '#ef4444' : adj.severity === 'MODERATE' ? '#f59e0b' : '#10b981',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      {adj.severity || 'INFO'}
                    </span>
                    <span style={{ fontSize: '0.95rem', fontWeight: '600' }}>
                      {adjustmentText}
                    </span>
                  </div>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    opacity: 0.95, 
                    paddingLeft: '0.5rem',
                    borderLeft: '3px solid rgba(255,255,255,0.3)',
                    marginLeft: '0.5rem'
                  }}>
                    <div style={{ marginBottom: '0.25rem' }}>
                      <strong>Issue:</strong> {adj.reason.replace(/_/g, ' ')}
                    </div>
                    <div>
                      <strong>What happened:</strong> {getExplanation(adj.reason)}
                    </div>
                    {adj.adjustment && (
                      <div style={{ marginTop: '0.25rem' }}>
                        <strong>AI Adjustment:</strong> {adj.adjustment}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '8px',
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span>💡</span>
            <span>
              These adjustments help the AI learn from mistakes and improve future trade recommendations.
              High severity issues get more weight in the learning process.
            </span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <button
          onClick={() => setActiveTab('overview')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'overview' ? stageColor : 'transparent',
            color: activeTab === 'overview' ? 'white' : '#64748b',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          📊 Overview
        </button>
        <button
          onClick={() => setActiveTab('pairs')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'pairs' ? stageColor : 'transparent',
            color: activeTab === 'pairs' ? 'white' : '#64748b',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          📈 Pair Win Rate Progression
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Overall Win Rate */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
            Overall Win Rate
          </div>
          <div style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold',
            color: overall_win_rate >= 60 ? '#10b981' : overall_win_rate >= 50 ? '#f59e0b' : '#ef4444'
          }}>
            {overall_win_rate.toFixed(1)}%
          </div>
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: '0.5rem' }}>
            Expected: {expected_win_rate}
          </div>
        </div>

        {/* Recent Win Rate */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
            Recent Win Rate (Last 50)
          </div>
          <div style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold',
            color: recent_win_rate >= 60 ? '#10b981' : recent_win_rate >= 50 ? '#f59e0b' : '#ef4444'
          }}>
            {recent_win_rate.toFixed(1)}%
          </div>
          <div style={{ fontSize: '0.85rem', color: improvement >= 0 ? '#10b981' : '#ef4444', marginTop: '0.5rem' }}>
            {improvement >= 0 ? '↑' : '↓'} {Math.abs(improvement).toFixed(1)}% from baseline
          </div>
        </div>

        {/* ML Impact */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
            ML Impact Estimated
          </div>
          <div style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold',
            color: ml_trained ? '#8b5cf6' : '#94a3b8'
          }}>
            {ml_trained ? `+${improvement.toFixed(1)}%` : '0%'}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: '0.5rem' }}>
            Expected: {expected_ml_impact}
          </div>
        </div>

        {/* Daily Stats */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
            Today's Performance
          </div>
          <div style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold',
            color: daily_stats.win_rate >= 60 ? '#10b981' : '#f59e0b'
          }}>
            {daily_stats.trades > 0 ? `${daily_stats.win_rate.toFixed(0)}%` : 'N/A'}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: '0.5rem' }}>
            {daily_stats.wins}W / {daily_stats.losses}L ({daily_stats.trades} trades)
          </div>
        </div>
      </div>

      {/* Milestones */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', color: '#1e293b' }}>
          🎯 Learning Milestones
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          {milestones.map((milestone, index) => (
            <div
              key={index}
              style={{
                padding: '1rem',
                borderRadius: '12px',
                background: milestone.achieved 
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : '#f1f5f9',
                color: milestone.achieved ? 'white' : '#64748b',
                textAlign: 'center',
                position: 'relative',
                transition: 'all 0.3s ease',
                border: milestone.achieved ? 'none' : '2px dashed #cbd5e1'
              }}
            >
              {milestone.achieved && (
                <div style={{ 
                  position: 'absolute', 
                  top: '-8px', 
                  right: '-8px',
                  background: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px'
                }}>
                  ✓
                </div>
              )}
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                {milestone.reward}
              </div>
              <div style={{ fontWeight: 'bold', fontSize: '1.2rem', marginBottom: '0.25rem' }}>
                {milestone.trades}
              </div>
              <div style={{ fontSize: '0.85rem', opacity: milestone.achieved ? 0.9 : 0.7 }}>
                {milestone.title}
              </div>
            </div>
          ))}
        </div>
        
        {next_milestone && (
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: '#eff6ff',
            borderRadius: '8px',
            color: '#1e40af'
          }}>
            <strong>Next Milestone:</strong> {next_milestone.trades} trades - {next_milestone.title}
            <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
              {next_milestone.trades - total_trades} trades to go!
            </div>
          </div>
        )}
      </div>

      {/* Win Rate Trend Chart */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', color: '#1e293b' }}>
          📈 Win Rate Trend (10-Trade Windows)
        </h2>
        
        {win_rate_trend.length > 0 ? (
          <div style={{ position: 'relative', height: '300px' }}>
            {/* Chart */}
            <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
              {/* Grid lines */}
              {[0, 25, 50, 75, 100].map((value) => (
                <g key={value}>
                  <line
                    x1="0"
                    y1={`${100 - value}%`}
                    x2="100%"
                    y2={`${100 - value}%`}
                    stroke="#e5e7eb"
                    strokeWidth="1"
                  />
                  <text
                    x="-5"
                    y={`${100 - value}%`}
                    fill="#94a3b8"
                    fontSize="12"
                    textAnchor="end"
                    dominantBaseline="middle"
                  >
                    {value}%
                  </text>
                </g>
              ))}
              
              {/* Trend line */}
              <polyline
                fill="none"
                stroke={stageColor}
                strokeWidth="3"
                points={win_rate_trend.map((point, index) => {
                  const x = (index / (win_rate_trend.length - 1)) * 100;
                  const y = 100 - point.win_rate;
                  return `${x}%,${y}%`;
                }).join(' ')}
              />
              
              {/* Points */}
              {win_rate_trend.map((point, index) => {
                const x = (index / (win_rate_trend.length - 1)) * 100;
                const y = 100 - point.win_rate;
                return (
                  <circle
                    key={index}
                    cx={`${x}%`}
                    cy={`${y}%`}
                    r="4"
                    fill="white"
                    stroke={stageColor}
                    strokeWidth="2"
                  >
                    <title>Trade {point.trade_number}: {point.win_rate}%</title>
                  </circle>
                );
              })}
            </svg>
            
            {/* X-axis label */}
            <div style={{ 
              textAlign: 'center', 
              marginTop: '1rem',
              color: '#64748b',
              fontSize: '0.9rem'
            }}>
              Trade Number
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>
            Not enough data yet. Complete at least 10 trades to see trend.
          </div>
        )}
      </div>

      {/* Next Steps */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1e293b' }}>
          🎯 Next Steps
        </h2>
        <ul style={{ 
          listStyle: 'none', 
          padding: 0, 
          margin: 0 
        }}>
          {next_steps.map((step, index) => (
            <li 
              key={index}
              style={{
                padding: '1rem',
                marginBottom: '0.5rem',
                background: index === 0 ? '#fef3c7' : '#f8fafc',
                borderRadius: '8px',
                borderLeft: index === 0 ? '4px solid #f59e0b' : '4px solid #cbd5e1',
                color: '#1e293b'
              }}
            >
              {index === 0 && '👉 '}{step}
            </li>
          ))}
        </ul>
      </div>

      {/* Refresh indicator */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '2rem',
        color: '#94a3b8',
        fontSize: '0.85rem'
      }}>
        Auto-refreshes every 60 seconds • Last updated: {new Date().toLocaleTimeString()}
      </div>
        </>
      )}

      {/* Pair Win Rate Progression Tab */}
      {activeTab === 'pairs' && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold', 
            marginBottom: '1.5rem', 
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            📈 Pair Win Rate Progression
          </h2>

          {pairProgressionData && pairProgressionData.pairs.length > 0 ? (
            <>
              <div style={{
                marginBottom: '1rem',
                color: '#64748b',
                fontSize: '0.9rem'
              }}>
                Showing {pairProgressionData.total_pairs} pairs • Sorted by win rate (best to worst)
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                {pairProgressionData.pairs.map((pair, index) => {
                  // Determine rank emoji
                  let rankEmoji = '';
                  if (index === 0) rankEmoji = '🥇';
                  else if (index === 1) rankEmoji = '🥈';
                  else if (index === 2) rankEmoji = '🥉';
                  else if (index === pairProgressionData.pairs.length - 1) rankEmoji = '⚠️';
                  
                  // Determine win rate color
                  let winRateColor = '#10b981'; // Green
                  if (pair.win_rate < 50) winRateColor = '#ef4444'; // Red
                  else if (pair.win_rate < 60) winRateColor = '#f59e0b'; // Orange
                  
                  // Determine progression color
                  let progressionColor = '#64748b'; // Gray
                  if (pair.progression > 0) progressionColor = '#10b981'; // Green
                  else if (pair.progression < 0) progressionColor = '#ef4444'; // Red
                  
                  return (
                    <div
                      key={pair.pair_raw}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '1rem 1.5rem',
                        background: index < 3 ? '#fef3c7' : '#f8fafc',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${winRateColor}`,
                        transition: 'transform 0.2s',
                        cursor: 'default'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(4px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                        {/* Rank */}
                        <div style={{ 
                          fontSize: '1.5rem',
                          minWidth: '2rem'
                        }}>
                          {rankEmoji || `#${index + 1}`}
                        </div>
                        
                        {/* Pair Name */}
                        <div style={{ 
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          color: '#1e293b',
                          minWidth: '100px'
                        }}>
                          {pair.pair}
                        </div>
                        
                        {/* Win/Loss Record */}
                        <div style={{
                          fontSize: '1rem',
                          color: '#64748b',
                          minWidth: '80px'
                        }}>
                          {pair.wins}/{pair.losses}
                        </div>
                        
                        {/* Win Rate */}
                        <div style={{
                          fontSize: '1.3rem',
                          fontWeight: 'bold',
                          color: winRateColor,
                          minWidth: '80px'
                        }}>
                          {pair.win_rate}%
                        </div>
                      </div>
                      
                      {/* 7-Day Progression */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.9rem',
                        color: progressionColor,
                        fontWeight: '600',
                        minWidth: '180px',
                        justifyContent: 'flex-end'
                      }}>
                        {pair.recent_trades > 0 ? (
                          <>
                            {pair.progression > 0 ? '📈' : pair.progression < 0 ? '📉' : '➡️'}
                            {pair.progression_display} in last 7 days
                          </>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>No recent trades</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary Stats */}
              <div style={{
                marginTop: '2rem',
                padding: '1rem',
                background: '#f1f5f9',
                borderRadius: '8px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '1rem'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.25rem' }}>
                    Best Performer
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#10b981' }}>
                    {pairProgressionData.pairs[0].pair} ({pairProgressionData.pairs[0].win_rate}%)
                  </div>
                </div>
                
                {pairProgressionData.pairs.length > 1 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.25rem' }}>
                      Needs Improvement
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#ef4444' }}>
                      {pairProgressionData.pairs[pairProgressionData.pairs.length - 1].pair} ({pairProgressionData.pairs[pairProgressionData.pairs.length - 1].win_rate}%)
                    </div>
                  </div>
                )}
                
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.25rem' }}>
                    Total Pairs
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#3b82f6' }}>
                    {pairProgressionData.total_pairs}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#94a3b8'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
              <div style={{ fontSize: '1.1rem' }}>No pair data available yet</div>
              <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                Complete some trades to see pair progression
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MLLearningProgress;
